import React from 'react';
import {
  ArrowRight,
  ClipboardCheck,
  FileText,
  ListChecks,
  Phone,
  UserCheck,
  Users,
  Video,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import CaseStudyProof, { caseStudyProofs } from '../../../Blog/CaseStudyProof';
import {
  CapabilityGrid,
  CONTACT_PATH,
  DEMO_PATH,
  FlowConnector,
  IndustryCta,
  IndustryFlowStyles,
  IndustryWorkflow,
  Section,
  SectionIntro,
} from '../Industries/industryUi';

const HERO_STEPS = [
  { label: 'Many applicants', note: 'A large inbound set' },
  { label: 'AI screening', note: 'Resumes read against the role' },
  { label: 'Assessment and interview', note: 'The checks this job needs' },
  { label: 'Qualified shortlist', note: 'A smaller set to review' },
  { label: 'Recruiter decision', note: 'A person decides' },
];

const TRADITIONAL = [
  'Applications',
  'Manual screening',
  'Calls',
  'Interview backlog',
  'Slow hiring',
];

const SEARCHJOB = [
  'Applications',
  'AI ranking',
  'Voice / WhatsApp screening',
  'Assessments',
  'AI interviews',
  'Qualified shortlist',
  'Recruiter decision',
];

const WORKFLOW = [
  { title: 'Applications', detail: 'Applicants arrive for the open role.', icon: Users },
  { title: 'AI Ranking', detail: 'Resumes are scored and ranked against the job description.', icon: ListChecks },
  { title: 'Voice / Candidate Screening', detail: 'A structured voice call covers role questions. WhatsApp records interest, CTC, and notice period.', icon: Phone },
  { title: 'Assessment', detail: 'The assessment matches this job.', icon: ClipboardCheck },
  { title: 'AI Interview', detail: 'A structured interview is saved for the team to review.', icon: Video },
  { title: 'Qualified Shortlist', detail: 'Recruiters see a smaller set, with the records attached.', icon: FileText },
  { title: 'Human Decision', detail: 'A person decides who moves forward.', icon: UserCheck },
];

const IDEAL_FOR = [
  'BPO',
  'Customer Service',
  'Retail',
  'Sales',
  'Delivery',
  'Operations',
  'Campus Hiring',
  'Seasonal Hiring',
  'Field Workforce',
];

const OUTCOME = [
  { label: 'Repetitive screening', note: 'Handled by AI' },
  { label: 'Qualified evidence', note: 'Records a recruiter can read' },
  { label: 'Recruiter review', note: 'A person checks the shortlist' },
  { label: 'Human decision', note: 'The hiring team selects' },
];

const AI_WORK = [
  'Ranking',
  'Screening',
  'Assessments',
  'First-round interviews',
  'Candidate evidence',
];

const HUMAN_WORK = [
  'Recruiter review',
  'Exceptions',
  'Judgment',
  'Final selection',
];

const PRODUCTS = [
  {
    title: 'AI Sourcing & Screening',
    helps: 'Score and rank resumes against the job description.',
    href: '/hirewithus/ai-sourcing-screening',
    icon: FileText,
  },
  {
    title: 'AI Call Screening',
    helps: 'A structured voice call. WhatsApp records interest, CTC, and notice period.',
    href: '/hirewithus/ai-call-screening',
    icon: Phone,
  },
  {
    title: 'Screening Assessment',
    helps: 'How the resume scores against the job description.',
    href: '/hirewithus/screening-assessment',
    icon: ClipboardCheck,
  },
  {
    title: 'Functional Assessment',
    helps: 'Behaviour, communication, and role-based skills.',
    href: '/hirewithus/functional-assessment',
    icon: Users,
  },
  {
    title: 'Virtual Hiring',
    helps: 'A structured AI interview the team can review.',
    href: '/hirewithus/virtualhiring',
    icon: Video,
  },
];

export default function HighVolumeHiring() {
  useSEO({
    title: 'High-Volume AI Recruitment | SearchJob.ai',
    description:
      'Screen and assess high volumes of applicants with AI resume ranking, voice screening, assessments and interviews.',
    canonical: 'https://searchjob.ai/solutions/high-volume-hiring',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <Compare />
        </ScrollReveal>
        <ScrollReveal>
          <Workflow />
        </ScrollReveal>
        <ScrollReveal>
          <IdealFor />
        </ScrollReveal>
        <ScrollReveal>
          <Outcome />
        </ScrollReveal>
        <ScrollReveal>
          <Products />
        </ScrollReveal>
        <ScrollReveal>
          <HumanLoop />
        </ScrollReveal>
        <ScrollReveal>
          <Proof />
        </ScrollReveal>
        <FinalCta />
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 pt-12 pb-16 sm:px-6 lg:grid-cols-2 lg:items-stretch lg:gap-14 lg:px-8 lg:pt-16">
        <div className="flex h-full flex-col justify-center rounded-3xl border border-primary bg-primary/5 p-6 text-center sm:p-8 lg:p-10 lg:text-left">
          <h1 className="mb-6 text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3.4rem] xl:text-6xl">
            10,000 Applicants. <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">One Hiring Team.</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl lg:mx-0">
            Screen at machine speed. Decide at human speed.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 lg:items-start lg:justify-start">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-4 text-base font-semibold !text-primary-foreground shadow-lg shadow-primary/25 group hover:!bg-primary/90"
            >
              <a href={DEMO_PATH}>
                Run a High-Volume Hiring Pilot
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!h-12 !rounded-full bg-card/70 px-4 text-base font-semibold backdrop-blur-md"
            >
              <a href="#high-volume-workflow">See the Workflow</a>
            </Button>
          </div>
        </div>
        <Funnel />
      </div>
    </section>
  );
}

function Funnel() {
  return (
    <div className="relative flex flex-col" aria-hidden="true">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[3rem] bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl" />
      <div className="flex flex-col rounded-3xl border border-primary bg-primary/5 px-5 py-5 lg:flex-1">
        <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">From volume to a shortlist</p>
        <ol className="lg:flex lg:flex-1 lg:flex-col">
          {HERO_STEPS.map((step, index) => (
            <li key={step.label} className="flex items-start gap-3 border-b border-primary/15 py-3 last:border-b-0 last:pb-0 first:pt-0 lg:flex-1">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-sm font-semibold text-primary">
                {index + 1}
              </span>
              <span>
                <span className="block text-sm font-semibold text-foreground">{step.label}</span>
                <span className="block text-sm text-muted-foreground">{step.note}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function Compare() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="How the work moves"
        title="The same applications. A shorter path to a person."
        copy="SearchJob.ai ranks and screens before a recruiter spends time. A person still decides."
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <PathList title="Traditional" items={TRADITIONAL} />
        <PathList title="SearchJob" items={SEARCHJOB} emphasized />
      </div>
      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
        WhatsApp screening here is the existing call-screening flow. It records interest, CTC, and notice period. It does not replace the recruiter decision.
      </p>
    </Section>
  );
}

function PathList({ title, items, emphasized = false }) {
  return (
    <article className={`rounded-3xl border p-5 sm:p-6 ${emphasized ? 'border-primary bg-primary/5' : 'border-primary/30 bg-primary/5'}`}>
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <ol className="mt-5 divide-y divide-primary/15">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" aria-hidden="true" />
            <span className="text-sm font-medium leading-relaxed text-foreground">{item}</span>
          </li>
        ))}
      </ol>
    </article>
  );
}

function Workflow() {
  return (
    <Section id="high-volume-workflow">
      <SectionIntro
        eyebrow="High-volume workflow"
        title="Large volume in. A shortlist a person can review."
        copy="Select a step. The path stays the same from the first application to the decision."
      />
      <IndustryWorkflow steps={WORKFLOW} />
    </Section>
  );
}

function IdealFor() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Ideal for"
        title="Roles that attract a large applicant set."
        copy="These are the situations this page is written for."
      />
      <ul className="flex flex-wrap gap-3">
        {IDEAL_FOR.map((item) => (
          <li key={item}>
            <span className="inline-flex min-h-11 items-center rounded-full border border-primary/30 bg-primary/5 px-4 text-sm font-medium text-foreground">
              {item}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Outcome() {
  return (
    <Section>
      <SectionIntro
        eyebrow="Business outcome"
        title="Less repetitive screening. The same human decision."
        copy="Reduce recruiter time spent on repetitive screening while keeping a person in control of the final decision."
      />
      <div className="rounded-3xl border border-primary bg-primary/5 p-5 sm:p-6">
        <ol className="grid items-center gap-3 lg:grid-cols-[minmax(0,1fr)_2rem_minmax(0,1fr)_2rem_minmax(0,1fr)_2rem_minmax(0,1fr)] lg:gap-4">
          {OUTCOME.map((step, index) => (
            <React.Fragment key={step.label}>
              <li className="h-full rounded-2xl border border-primary/20 bg-background px-4 py-3">
                <span className="block text-sm font-semibold text-foreground">{step.label}</span>
                <span className="block text-sm text-muted-foreground">{step.note}</span>
              </li>
              {index < OUTCOME.length - 1 ? <FlowConnector /> : null}
            </React.Fragment>
          ))}
        </ol>
      </div>
    </Section>
  );
}

function Products() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Relevant capabilities"
        title="Products already on SearchJob.ai."
        copy="Ranking sits inside AI Sourcing and Screening. Voice and WhatsApp screening sit inside AI Call Screening."
      />
      <CapabilityGrid items={PRODUCTS} />
    </Section>
  );
}

function HumanLoop() {
  return (
    <Section>
      <SectionIntro
        eyebrow="AI and human control"
        title="AI handles repetitive screening. Humans make the final hiring decision."
        copy="SearchJob.ai collects evidence. It does not reject or hire on its own."
      />
      <div className="grid gap-4 md:grid-cols-2">
        <WorkList title="AI handles" items={AI_WORK} />
        <WorkList title="Your team controls" items={HUMAN_WORK} />
      </div>
    </Section>
  );
}

function WorkList({ title, items }) {
  return (
    <article className="rounded-2xl border border-primary bg-primary/5 p-5">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <ul className="mt-5 space-y-3">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
    </article>
  );
}

function Proof() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Customer proof"
        title="A published surge-hiring story."
        copy="Grand Gaming is the published case study for hiring when volume increases. The page does not add numbers beyond that story."
      />
      <CaseStudyProof items={[caseStudyProofs.grandGaming]} />
    </Section>
  );
}

function FinalCta() {
  return (
    <IndustryCta
      title="Run a High-Volume Hiring Pilot"
      copy="Use SearchJob.ai to screen, assess and interview large applicant volumes before your recruiters spend time on final decisions."
      primaryLabel="Run a High-Volume Hiring Pilot"
      primaryHref={DEMO_PATH}
      secondaryLabel="Talk to a Hiring Expert"
      secondaryHref={CONTACT_PATH}
    />
  );
}
